import { logger, Logger } from './logger';

/**
 * Creates a scoped logger for a specific component or module
 * 
 * @param scope - The scope name for the logger (usually component or module name)
 * @returns An object with logger methods that automatically include the scope
 */
export function createScopedLogger(scope: string): {
  debug: (message: string, ...args: unknown[]) => void;
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string | Error, ...args: unknown[]) => void;
} {
  return {
    debug: (message: string, ...args: unknown[]) => {
      logger.debug(message, scope, ...args);
    },
    info: (message: string, ...args: unknown[]) => {
      logger.info(message, scope, ...args);
    },
    warn: (message: string, ...args: unknown[]) => {
      logger.warn(message, scope, ...args);
    },
    error: (message: string | Error, ...args: unknown[]) => {
      logger.error(message, scope, ...args);
    }
  };
}

// Store log timestamps to implement debouncing
const logTimestamps: Record<string, Record<string, number>> = {};
const LOG_DEBOUNCE_MS = 5000; // Only log the same message from the same source once every 5 seconds

/**
 * Creates a hook-style logger for React components with built-in debouncing
 * to prevent excessive logging that could trigger rate limits
 * 
 * @param componentName - The component name for the logger
 * @returns A logger object with scope set to the component name and debounced methods
 */
export function useLogger(componentName: string) {
  const baseLogger = createScopedLogger(componentName);
  
  // Initialize component's log timestamp record if not exists
  if (!logTimestamps[componentName]) {
    logTimestamps[componentName] = {};
  }
  
  return {
    debug: (message: string, ...args: unknown[]) => {
      const key = `debug:${message}`;
      const now = Date.now();
      // Skip if the same message was logged recently
      if (now - (logTimestamps[componentName][key] || 0) < LOG_DEBOUNCE_MS) {
        return;
      }
      logTimestamps[componentName][key] = now;
      baseLogger.debug(message, ...args);
    },
    info: (message: string, ...args: unknown[]) => {
      const key = `info:${message}`;
      const now = Date.now();
      if (now - (logTimestamps[componentName][key] || 0) < LOG_DEBOUNCE_MS) {
        return;
      }
      logTimestamps[componentName][key] = now;
      baseLogger.info(message, ...args);
    },
    warn: (message: string, ...args: unknown[]) => {
      const key = `warn:${message}`;
      const now = Date.now();
      if (now - (logTimestamps[componentName][key] || 0) < LOG_DEBOUNCE_MS) {
        return;
      }
      logTimestamps[componentName][key] = now;
      baseLogger.warn(message, ...args);
    },
    error: (message: string | Error, ...args: unknown[]) => {
      // For errors, create a key based on the error message/type
      const errorMsg = message instanceof Error ? message.message : message;
      const key = `error:${errorMsg}`;
      const now = Date.now();
      if (now - (logTimestamps[componentName][key] || 0) < LOG_DEBOUNCE_MS) {
        return;
      }
      logTimestamps[componentName][key] = now;
      baseLogger.error(message, ...args);
    }
  };
} 